import { useState, useRef, useEffect, useMemo } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import Layout from "../../components/Layout";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ArrowDownNarrowWide, ArrowUpNarrowWide } from "lucide-react";
import { useVideoActions } from "../../hooks/useVideoActions";
import { formatTimestampForDisplay } from "../../lib/formatTimestamp";

interface Movement {
  id: number;
  movement_type: string;
  start_time: number;
  end_time: number;
  confidence_score?: number;
  metadata?: any;
}

interface Video {
  id: number;
  title: string;
  filename?: string | null;
  description: string;
  created_at: string;
  recorded_at?: string;
  video_url_lowres?: string;
  video_url_original?: string;
  analysis_status: string;
  analysis_type?: string;
  movements_count: number;
  movements?: Movement[];
}

interface Props {
  videos: Video[];
}

type SortMode = "uploaded" | "recorded" | "filename";
type SortOrder = "asc" | "desc";

const DEFAULT_SORT_MODE: SortMode = "recorded";
const DEFAULT_SORT_ORDER: SortOrder = "desc";

const parseDate = (value?: string) => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getSortTimestamp = (video: Video, mode: SortMode) => {
  if (mode === "uploaded") {
    return parseDate(video.created_at) ?? Number.NEGATIVE_INFINITY;
  }

  const recordedTimestamp = parseDate(video.recorded_at);
  if (recordedTimestamp !== null) {
    return recordedTimestamp;
  }

  return parseDate(video.created_at) ?? Number.NEGATIVE_INFINITY;
};

const getComparableFilename = (video: Video) => {
  const name = (video.filename ?? video.title ?? "").trim();
  return name.toLocaleLowerCase();
};

const sortVideos = (videoList: Video[], mode: SortMode, order: SortOrder) => {
  if (mode === "filename") {
    return [...videoList].sort((a, b) => {
      const nameA = getComparableFilename(a);
      const nameB = getComparableFilename(b);

      const hasNameA = nameA.length > 0;
      const hasNameB = nameB.length > 0;

      if (hasNameA && hasNameB) {
        const comparison = nameA.localeCompare(nameB, undefined, {
          sensitivity: "base",
          numeric: true,
        });
        if (comparison !== 0) {
          return order === "asc" ? comparison : -comparison;
        }
      } else if (hasNameA || hasNameB) {
        if (order === "asc") {
          return hasNameA ? -1 : 1;
        }
        return hasNameA ? 1 : -1;
      }

      const timestampA = getSortTimestamp(a, "uploaded");
      const timestampB = getSortTimestamp(b, "uploaded");
      return order === "asc"
        ? timestampA - timestampB
        : timestampB - timestampA;
    });
  }

  return [...videoList].sort((a, b) => {
    const timestampA = getSortTimestamp(a, mode);
    const timestampB = getSortTimestamp(b, mode);
    return order === "asc"
      ? timestampA - timestampB
      : timestampB - timestampA;
  });
};

export default function VideosIndex({ videos: initialVideos }: Props) {
  const { url } = usePage();
  const [sortBy, setSortBy] = useState<SortMode>(DEFAULT_SORT_MODE);
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [videoList, setVideoList] = useState(initialVideos);
  const sortedVideos = useMemo(
    () => sortVideos(videoList, sortBy, sortOrder),
    [videoList, sortBy, sortOrder],
  );

  // Get video ID from URL parameter
  const urlParams = new URLSearchParams(url.split("?")[1] || "");
  const focusedVideoId = urlParams.get("video");

  const [selectedVideo, setSelectedVideo] = useState<Video | null>(() => {
    if (focusedVideoId) {
      const foundVideo = initialVideos.find(
        (v) => v.id.toString() === focusedVideoId,
      );
      if (foundVideo) return foundVideo;
    }
    const initiallySorted = sortVideos(initialVideos, sortBy, DEFAULT_SORT_ORDER);
    return initiallySorted.length > 0 ? initiallySorted[0] : null;
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playbackSpeeds = useMemo(() => [0.25, 0.5, 0.75, 1], []);

  const { deleteVideo } = useVideoActions({
    onSuccess: () => {
      setDeleteDialogOpen(false);
      if (videoToDelete) {
        setVideoList((prev) => prev.filter((v) => v.id !== videoToDelete.id));
      }
      setVideoToDelete(null);
    },
    onError: (errors) => {
      console.error("Delete failed:", errors);
      alert("Failed to delete video: " + errors.join(", "));
    },
  });

  const handleDeleteClick = (video: Video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (videoToDelete) {
      deleteVideo(videoToDelete.id);
    }
  };

  const handleVideoSelect = (video: Video) => {
    setSelectedVideo(video);
    router.visit(window.location.pathname, {
      data: { video: video.id },
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  };

  useEffect(() => {
    setVideoList(initialVideos);
  }, [initialVideos]);

  useEffect(() => {
    if (selectedVideo) {
      const stillExists = videoList.some((v) => v.id === selectedVideo.id);
      if (!stillExists) {
        const nextVideo = sortedVideos[0] ?? null;
        setSelectedVideo(nextVideo);
      }
    } else if (sortedVideos.length > 0) {
      const nextVideo = sortedVideos[0];
      setSelectedVideo(nextVideo);
    }
  }, [selectedVideo, videoList, sortedVideos]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackRate;
  }, [playbackRate, selectedVideo]);

  const getCurrentVideoUrl = () => {
    if (!selectedVideo) return undefined;
    return selectedVideo.video_url_original ?? undefined;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, "0")}`;
  };

  const getAnalysisStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-50";
      case "processing":
        return "text-blue-600 bg-blue-50";
      case "failed":
        return "text-red-600 bg-red-50";
      default:
        return "text-yellow-600 bg-yellow-50";
    }
  };

  const getListDateLabel = (video: Video) => {
    const uploadedLabel =
      formatTimestampForDisplay(video.created_at) ?? video.created_at;

    if (sortBy === "uploaded") {
      return uploadedLabel
        ? `Uploaded ${uploadedLabel}`
        : "Upload date unavailable";
    }

    const recordedLabel = formatTimestampForDisplay(video.recorded_at);
    if (recordedLabel) {
      return `Recorded ${recordedLabel}`;
    }

    return uploadedLabel
      ? `Uploaded ${uploadedLabel}`
      : "Recorded date unavailable";
  };

  const getDisplayTimestamp = (value?: string | null) => {
    return formatTimestampForDisplay(value) ?? value?.trim() ?? null;
  };

  return (
    <Layout>
      <div className="overflow-hidden p-6 h-full sm:px-6 lg:px-8">
        {videoList.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <Card>
              <CardContent className="py-12 text-center">
                <h3 className="mb-2 text-lg font-semibold">No videos yet</h3>
                <p className="mb-4 text-muted-foreground">
                  Upload your first golf video to get started
                </p>
                <Button asChild>
                  <Link href="/videos/new">Upload Video</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid h-full min-h-0 max-h-full grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)_minmax(14rem,20rem)] gap-6">
            {/* Left Sidebar - Video List */}
            <div className="flex flex-col min-h-0">
              <Card className="flex overflow-hidden flex-col flex-1">
                <CardHeader className="flex-shrink-0 pb-4 space-y-4">
                  <div>
                    <CardTitle className="text-lg">All Videos</CardTitle>
                    <CardDescription>
                      {videoList.length} video
                      {videoList.length !== 1 ? "s" : ""}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center text-xs">
                    <span className="font-medium uppercase text-muted-foreground">
                      Sort by
                    </span>
                    <Select
                      value={sortBy}
                      onValueChange={(value: SortMode) => setSortBy(value)}
                    >
                      <SelectTrigger className="w-[180px] h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recorded">Recorded date</SelectItem>
                        <SelectItem value="uploaded">Upload date</SelectItem>
                        <SelectItem value="filename">File name</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() =>
                        setSortOrder((prev) =>
                          prev === "asc" ? "desc" : "asc",
                        )
                      }
                      aria-label={`Sort ${sortOrder === "asc" ? "descending" : "ascending"}`}
                    >
                      {sortOrder === "asc" ? (
                        <ArrowUpNarrowWide className="h-4 w-4" />
                      ) : (
                        <ArrowDownNarrowWide className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="overflow-hidden flex-1 p-0">
                  <div className="flex overflow-y-auto flex-col px-4 py-4 space-y-2 h-full">
                    {sortedVideos.map((video) => (
                      <div
                        key={video.id}
                        onClick={() => handleVideoSelect(video)}
                        className={`touch-manipulation cursor-pointer border p-3 transition-all hover:bg-muted/50 active:scale-95 ${
                          selectedVideo?.id === video.id
                            ? "border-primary/20 bg-primary/10"
                            : "border-transparent"
                        }`}
                      >
                        <div className="space-y-1">
                          <h4 className="text-sm font-medium truncate">
                            {video.title}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {getListDateLabel(video)}
                          </p>
                          <div className="flex justify-between items-center">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium ${getAnalysisStatusColor(
                                video.analysis_status,
                              )}`}
                            >
                              {video.analysis_status}
                            </span>
                            {video.movements_count > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {video.movements_count} movements
                              </span>
                            )}
                          </div>
                          {video.description && (
                            <p className="text-xs line-clamp-2 text-muted-foreground">
                              {video.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Center - Video Player */}
            <div className="flex flex-col min-h-0">
              {selectedVideo ? (
                <Card className="flex flex-col flex-1 min-h-0">
                  <CardHeader className="flex-shrink-0 py-3">
                    {/* Title and Metadata Section */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base truncate">
                            {selectedVideo.title}
                          </CardTitle>
                          <CardDescription className="mt-0.5 text-xs">
                            {(() => {
                              const uploadedTimestamp = getDisplayTimestamp(
                                selectedVideo.created_at,
                              );
                              const recordedTimestamp = getDisplayTimestamp(
                                selectedVideo.recorded_at,
                              );

                              return (
                                <>
                                  {uploadedTimestamp && (
                                    <>Uploaded {uploadedTimestamp}</>
                                  )}
                                  {recordedTimestamp && (
                                    <> • Recorded {recordedTimestamp}</>
                                  )}
                                </>
                              );
                            })()}
                          </CardDescription>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteClick(selectedVideo)}
                        >
                          Delete
                        </Button>
                      </div>

                      {/* Controls Section */}
                      <div className="flex flex-wrap gap-x-4 gap-y-2 items-center pt-1.5 border-t">
                        {/* Status Badge */}
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${getAnalysisStatusColor(
                            selectedVideo.analysis_status,
                          )}`}
                        >
                          {selectedVideo.analysis_status}
                        </span>

                        {/* Playback Speed Controls */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-muted-foreground uppercase">
                            Speed
                          </span>
                          <div className="flex gap-1">
                            {playbackSpeeds.map((speed) => (
                              <Button
                                key={speed}
                                size="sm"
                                variant={
                                  playbackRate === speed ? "default" : "outline"
                                }
                                onClick={() => setPlaybackRate(speed)}
                                className="h-7 px-2"
                              >
                                {speed}×
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Download Button */}
                        {getCurrentVideoUrl() && (
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                          >
                            <a
                              href={getCurrentVideoUrl()}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Download
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex overflow-hidden flex-1 justify-center items-center p-4 min-h-0">
                    {getCurrentVideoUrl() ? (
                      <video
                        ref={videoRef}
                        key={selectedVideo.id}
                        src={getCurrentVideoUrl()}
                        controls
                        className="object-contain max-w-full max-h-full bg-black"
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="flex justify-center items-center w-full h-full bg-muted">
                        <p className="text-muted-foreground">
                          Video not available
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full">
                  <CardContent className="flex justify-center items-center h-full">
                    <div className="text-center">
                      <h3 className="mb-2 text-lg font-semibold">
                        Select a video
                      </h3>
                      <p className="text-muted-foreground">
                        Choose a video from the sidebar to view it here
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Side - Video Details and Movements */}
            <div className="flex flex-col space-y-4 min-h-0">
              {selectedVideo ? (
                <div className="flex flex-col space-y-4 h-full min-h-0">
                  {/* Video Details */}
                  {selectedVideo.description && (
                    <Card className="flex-shrink-0">
                      <CardHeader>
                        <CardTitle className="text-lg">Description</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {selectedVideo.description}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Detected Movements */}
                  {selectedVideo.movements &&
                    selectedVideo.movements.length > 0 && (
                      <Card className="flex overflow-hidden flex-col flex-1 min-h-0">
                        <CardHeader className="flex-shrink-0">
                          <CardTitle className="text-lg">
                            Detected Movements ({selectedVideo.movements.length}
                            )
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex overflow-hidden flex-col flex-1 p-0">
                          <div className="overflow-y-auto flex-1 px-4 py-4">
                            <div className="space-y-3">
                              {selectedVideo.movements.map(
                                (movement, index) => (
                                  <div
                                    key={movement.id}
                                    className="flex justify-between items-center p-3 border bg-muted/50"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="flex justify-center items-center w-6 h-6 text-xs font-medium bg-primary text-primary-foreground">
                                        {index + 1}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium">
                                          {movement.movement_type
                                            .replace("_", " ")
                                            .toUpperCase()}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {formatTime(movement.start_time)} -{" "}
                                          {formatTime(movement.end_time)}
                                        </p>
                                        {movement.confidence_score && (
                                          <p className="text-xs text-muted-foreground">
                                            {Math.round(
                                              movement.confidence_score * 100,
                                            )}
                                            % confidence
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const video = document.querySelector(
                                          "video",
                                        ) as HTMLVideoElement;
                                        if (video) {
                                          video.currentTime =
                                            movement.start_time;
                                        }
                                      }}
                                    >
                                      View
                                    </Button>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                </div>
              ) : (
                <Card className="h-full">
                  <CardContent className="flex justify-center items-center h-full">
                    <div className="text-center">
                      <h3 className="mb-2 text-lg font-semibold">
                        Video Details
                      </h3>
                      <p className="text-muted-foreground">
                        Select a video to view details and movements
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Video</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{videoToDelete?.title}"? This
                action cannot be undone and will permanently remove the video
                file from storage.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                Delete Video
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
